import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, ArrowLeft } from "lucide-react";
import { useSubscription, SUBSCRIPTION_TIERS } from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";

const Subscription = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { subscribed, currentTier, generationCount, createCheckoutSession, openCustomerPortal, loading } = useSubscription();

  const handleSubscribe = async (priceId: string) => {
    try {
      await createCheckoutSession(priceId);
      toast({
        title: "Redirecting to checkout",
        description: "You'll be redirected to Stripe checkout in a new tab.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      await openCustomerPortal();
      toast({
        title: "Opening customer portal",
        description: "You'll be redirected to manage your subscription in a new tab.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to open customer portal. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 p-4 pt-8 md:pt-12">
      <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
          <p className="text-xl text-muted-foreground">
            {subscribed 
              ? `You're currently on the ${currentTier?.name} plan`
              : "Start with 3 free generations, then unlock unlimited AI hairstyle transformations"}
          </p>
          {!loading && (
            <p className="text-lg mt-2">
              Generations used: <span className="font-bold">{generationCount.used}/{generationCount.limit}</span>
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {/* Free Tier */}
          <Card className={!subscribed ? "border-primary shadow-lg" : ""}>
            <CardHeader>
              <CardTitle>Free</CardTitle>
              <CardDescription>Try it out</CardDescription>
              <div className="text-3xl font-bold mt-4">$0</div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  3 AI generations
                </li>
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  All hairstyles available
                </li>
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  High quality results
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              {!subscribed ? (
                <div className="w-full text-center py-2 font-semibold text-primary">
                  Current Plan
                </div>
              ) : null}
            </CardFooter>
          </Card>

          {/* Monthly */}
          <Card className={subscribed && currentTier?.productId === SUBSCRIPTION_TIERS.monthly.productId ? "border-primary shadow-lg" : ""}>
            <CardHeader>
              <CardTitle>{SUBSCRIPTION_TIERS.monthly.name}</CardTitle>
              <CardDescription>Best for regular users</CardDescription>
              <div className="text-3xl font-bold mt-4">{SUBSCRIPTION_TIERS.monthly.price}</div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  90 generations per month
                </li>
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  All hairstyles available
                </li>
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  Priority support
                </li>
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  Cancel anytime
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              {subscribed && currentTier?.productId === SUBSCRIPTION_TIERS.monthly.productId ? (
                <div className="w-full">
                  <div className="text-center py-2 font-semibold text-primary mb-2">
                    Current Plan
                  </div>
                  <Button 
                    onClick={handleManageSubscription}
                    variant="outline"
                    className="w-full"
                  >
                    Manage Subscription
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => handleSubscribe(SUBSCRIPTION_TIERS.monthly.priceId)}
                  className="w-full"
                  disabled={loading}
                >
                  Subscribe
                </Button>
              )}
            </CardFooter>
          </Card>

          {/* Annual */}
          <Card className={`relative ${subscribed && currentTier?.productId === SUBSCRIPTION_TIERS.annual.productId ? "border-primary shadow-lg" : "border-primary"}`}>
            <div className="absolute -top-4 left-0 right-0 flex justify-center z-10">
              <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-semibold shadow-md">
                {SUBSCRIPTION_TIERS.annual.savings}
              </span>
            </div>
            <CardHeader className="pt-10">
              <CardTitle>{SUBSCRIPTION_TIERS.annual.name}</CardTitle>
              <CardDescription>Best value</CardDescription>
              <div className="text-3xl font-bold mt-4">{SUBSCRIPTION_TIERS.annual.price}</div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  90 generations per month
                </li>
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  All hairstyles available
                </li>
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  Priority support
                </li>
                <li className="flex items-center">
                  <Check className="mr-2 h-4 w-4 text-primary" />
                  Save 30% vs monthly
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              {subscribed && currentTier?.productId === SUBSCRIPTION_TIERS.annual.productId ? (
                <div className="w-full">
                  <div className="text-center py-2 font-semibold text-primary mb-2">
                    Current Plan
                  </div>
                  <Button 
                    onClick={handleManageSubscription}
                    variant="outline"
                    className="w-full"
                  >
                    Manage Subscription
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => handleSubscribe(SUBSCRIPTION_TIERS.annual.priceId)}
                  className="w-full"
                  disabled={loading}
                >
                  Subscribe
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>

        {subscribed && (
          <div className="mt-8 text-center">
            <Button
              onClick={handleManageSubscription}
              variant="outline"
            >
              Manage My Subscription
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscription;
